import React, {
  forwardRef,
  useCallback,
  FC,
  ReactElement,
  ReactNode,
} from 'react';
import classnames from 'classnames';
import { StaticContext } from 'react-router-dom';
import { NavLink, NavLinkProps } from 'react-router-dom';
import MenuItem, { MenuItemProps } from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Tooltip from '@mui/material/Tooltip';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Typography } from '@mui/material';

interface Props {
  leftIcon?: ReactElement;
  primaryText?: ReactNode;
  staticContext?: StaticContext;
  sidebarIsOpen: boolean;
}

export type MenuItemLinkProps = Props &
  NavLinkProps &
  MenuItemProps<'li', { button?: true }>;

const NavLinkRef = forwardRef<HTMLAnchorElement, NavLinkProps>((props, ref) => (
  <NavLink innerRef={ref} {...props} />
));
NavLinkRef.displayName = 'NavLinkRef';

const useStyles = makeStyles(theme => createStyles({
  root: {
    color: theme.palette.text.secondary,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1, 0, 1, 0)
  },
  active: {
    color: theme.palette.primary.main,
    borderLeftColor: theme.palette.primary.main,
  },
  icon: {
    marginBottom: theme.spacing(0.5),
    color: 'inherit',
    justifyContent: 'center',
  },
}),
{ name: 'OrbitMenuItemLink' }
);

const MenuItemLink: FC<MenuItemLinkProps> = forwardRef((props, ref) => {
  const {
    className,
    primaryText,
    leftIcon,
    onClick,
    sidebarIsOpen,
    ...rest
  } = props;
  const classes = useStyles(props);

  const handleMenuTap = useCallback(
    e => {
      onClick && onClick(e);
    },
    [onClick]
  );

  const renderMenuItem = () => {
    return (
      <MenuItem
        className={classnames(classes.root, className)}
        activeClassName={classes.active}
        component={NavLinkRef}
        ref={ref}
        {...rest}
        onClick={handleMenuTap}
      >
        <ListItemIcon className={classes.icon} >
          {leftIcon}
        </ListItemIcon>
        <Typography variant='caption'>{primaryText}</Typography>
      </MenuItem>
    );
  };

  if (sidebarIsOpen) {
    return renderMenuItem();
  }

  return (
    <Tooltip title={primaryText} placement="right">
      {renderMenuItem()}
    </Tooltip>
  );
});

MenuItemLink.displayName = 'MenuItemLink';

export default MenuItemLink;
